// Plain-text version of every public page. Used at build time to write crawler-readable HTML
// (see vite.config.ts) and to keep llms.txt honest. No asset imports allowed here.
import { SITE_URL, PHONE_DISPLAY, RESULTS, FAQS, LOCATIONS, AWARDS } from "./content";
import { VIDEO_TYPES } from "./video-types";

export type SeoPage = {
  path: string;
  title: string;
  description: string;
  h1: string;
  /** 40-60 word answer-first summary that AI tools can quote */
  summary: string;
  sections?: { h: string; p: string }[];
  faq?: readonly { q: string; a: string }[];
  schema?: object[];
};

export const BUSINESS_SUMMARY =
  "Stag Marketing Group is a video marketing agency based in Wrexham, North Wales. We plan, film and edit video in-house, then run Meta ads that turn it into enquiries for local businesses across North Wales, Cheshire and Shropshire. Rolling monthly, no tie-in, and only one business per industry per area.";

const AREAS = Object.values(LOCATIONS).map((l) => l.town);

export const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": ["ProfessionalService", "LocalBusiness"],
  "@id": `${SITE_URL}/#business`,
  name: "Stag Marketing Group",
  legalName: "Stag Marketing Group Ltd",
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.png`,
  image: `${SITE_URL}/og-image.png`,
  description: BUSINESS_SUMMARY,
  telephone: "+447403682194",
  slogan: "Straight talking. Great video. Real results.",
  foundingDate: "2025",
  founder: { "@type": "Person", name: "Oshan Bennett" },
  address: { "@type": "PostalAddress", addressLocality: "Wrexham", addressRegion: "Wales", addressCountry: "GB" },
  areaServed: AREAS.map((name) => ({ "@type": "Place", name })),
  knowsAbout: ["Videography", "Video production", "Commercial video", "Drone videography", "Meta ads", "Facebook ads", "Lead generation", "Social media video"],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Video and lead generation services",
    itemListElement: ["Video Growth Engine (video lead generation)", "Company profile videos", "Video adverts", "Testimonial videos", "Website videos", "Drone videography", "Website design and SEO"].map((name) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name } })),
  },
  sameAs: ["https://www.instagram.com/stagmarketinggroup", "https://www.linkedin.com/company/stag-marketing-group"],
  aggregateRating: { "@type": "AggregateRating", ratingValue: "5", reviewCount: "16", bestRating: "5" },
};

const service = (name: string, description: string, areas: string[] = AREAS) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  serviceType: name,
  description,
  provider: { "@id": `${SITE_URL}/#business` },
  areaServed: areas.map((n) => ({ "@type": "Place", name: n })),
});

const crumbs = (items: [string, string][]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [["Home", "/"], ...items].map(([name, path], i) => ({ "@type": "ListItem", position: i + 1, name, item: `${SITE_URL}${path}` })),
});

const resultLines = RESULTS.map((r) => ({ h: `${r.stat} ${r.unit.toLowerCase()}`, p: r.label }));

export const SEO_PAGES: SeoPage[] = [
  {
    path: "/",
    title: "Video Marketing Agency Wrexham & North Wales | Stag",
    description: "Video production and Meta ads that bring local businesses real leads. Filmed and edited in-house across North Wales and Shropshire. Call or WhatsApp us.",
    h1: "Video that brings local businesses more customers",
    summary: BUSINESS_SUMMARY,
    sections: [
      { h: "Video Growth Engine", p: "Our monthly video lead generation service: one filming day, short-form videos, Meta ads to local people, social posting and first-message follow-up with new followers." },
      { h: "Commercial video production", p: "Company profiles, adverts, testimonials, website videos and drone footage, planned, filmed and edited in-house." },
      { h: "UK Small Business Awards 2026 finalists", p: `${AWARDS.summary} ${AWARDS.description} Awards website: ${AWARDS.url}` },
      ...resultLines,
    ],
    faq: FAQS,
  },
  {
    path: "/video-growth-engine",
    title: "Facebook & Meta Ads Agency Wrexham | Video Lead Generation | Stag",
    description: "Facebook and Instagram ads with video we film for you. Lead generation and social media marketing for local businesses in Wrexham, North Wales and Shropshire.",
    h1: "Facebook ads that bring in leads. Every month.",
    summary: "Stag is a Facebook and Meta ads agency in Wrexham. The Video Growth Engine is our monthly lead generation service. We film your business, edit 9 organic videos and an ad each month, run Meta ads to people in your area and message every new follower. Only one business per industry per area. Rolling monthly, no tie-in.",
    sections: [
      { h: "What's included", p: "One filming day a month, a video sales letter ad, 9 organic videos, carousels, Meta ads management, social posting, first-message follow-up with new followers and monthly ad optimisation." },
      { h: "Area exclusivity", p: "We only take one business per industry in each area, so we never run ads for your direct competitor." },
      ...resultLines,
    ],
    faq: FAQS,
    schema: [service("Video lead generation", "Monthly short-form video, Meta ads, social media management and follower follow-up for local businesses.")],
  },
  {
    path: "/commercial-video",
    title: "Video Production North Wales & Shropshire | Stag",
    description: "Company profiles, testimonials, video adverts, website videos and drone footage for local businesses. Planned, filmed and edited in-house. Get a quote today.",
    h1: "Commercial video production in North Wales and Shropshire",
    summary: "Stag is a commercial videographer in Wrexham making company profile films, video adverts, testimonials, website videos and drone footage for businesses across North Wales, Cheshire and Shropshire. Everything is planned, filmed and edited in-house, and every project is quoted individually.",
    sections: [
      { h: "Company profile videos", p: "A short film about who you are and why customers should trust you. Ideal for your website, quotes and social media." },
      { h: "Video adverts", p: "Short, punchy ads built for Facebook and Instagram that get local people to call." },
      { h: "Testimonial videos", p: "Happy customers on camera, filmed properly and kept short." },
      { h: "Drone videography", p: "Aerial footage of sites, venues and projects, flown within UK drone rules." },
    ],
    schema: [service("Commercial video production", "Company profile films, adverts, testimonials, website videos and drone footage for local businesses."), service("Drone videography", "Aerial video for construction sites, venues, property and events.")],
  },
  {
    path: "/our-results",
    title: "Our Work & Results | Video Case Studies | Stag",
    description: "See the videos we have made and the results behind them: 461% ROI, 300+ leads in a month and more for local businesses in North Wales and Shropshire.",
    h1: "Our work and results",
    summary: "Stag's video and Meta ads work has delivered a 461% return on investment for one client, a £10,000 enquiry within 24 hours for another, and took a third from 1 lead a month to 10 leads a day. Here are the videos and the numbers behind them.",
    sections: resultLines,
  },
  {
    path: "/other-services",
    title: "Website Design, SEO & Graphic Design | Stag",
    description: "Website design, SEO, graphic design, print and marketing strategy to support your video. One local team for North Wales and Shropshire businesses.",
    h1: "Website design, SEO and graphic design",
    summary: "Alongside video, Stag builds fast websites, handles SEO, designs logos and print, and plans marketing for local businesses in North Wales and Shropshire. It all supports the same goal: more enquiries from the people your video reaches. Every project is quoted individually.",
  },
  {
    path: "/about",
    title: "About Stag | Wrexham Video Marketing and Meta Ads Team",
    description: "Meet Stag, a straight talking Wrexham video team helping local businesses across North Wales and Shropshire win more customers with video that works.",
    h1: "About Stag Marketing Group",
    summary: "Stag Marketing Group is a Wrexham video marketing team founded by Oshan Bennett. We film real local businesses, put ad spend behind the best videos and judge ourselves on enquiries, not likes. No fluff, no jargon.",
    sections: [{ h: "UK Small Business Awards 2026 finalists", p: `${AWARDS.summary} ${AWARDS.description} Awards website: ${AWARDS.url}` }],
  },
  ...Object.entries(LOCATIONS).map(([slug, l]) => ({
    path: `/${slug}`,
    title: l.title,
    description: l.description,
    h1: l.h1,
    summary: `${l.intro} Stag is a Wrexham-based videographer and video marketing agency. Call or WhatsApp ${PHONE_DISPLAY}.`,
    sections: [...l.sections, { h: `Areas near ${l.town} we cover`, p: l.nearby.join(", ") }],
    faq: "faq" in l ? l.faq : undefined,
    schema: [service(`Videographer ${l.town}`, l.description, [l.town, ...l.nearby]), crumbs([[l.h1, `/${slug}`]])],
  })),
  ...VIDEO_TYPES.map((v) => ({
    path: `/${v.slug}`,
    title: v.title,
    description: v.description,
    h1: v.h1,
    summary: `${v.intro} Stag Marketing Group plans, films and edits ${v.name.toLowerCase()} in-house from Wrexham for businesses across North Wales, Cheshire and Shropshire. Call or WhatsApp ${PHONE_DISPLAY}.`,
    sections: [...v.benefits, { h: "Where it gets used", p: v.uses.join(", ") }],
    faq: v.faq,
    schema: [service(v.name, v.description), crumbs([["Video production", "/commercial-video"], [v.name, `/${v.slug}`]])],
  })),
];
